import { inject, Injectable } from '@angular/core';
import { SearchResultProvider } from '@softapps/core';
import { ApiDataAccess, Query } from '@softline/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DocumentGridDataSource } from '../grid/query/document-grid.data-source';
import { getDocumentQueryTypePrefix } from '../helper/document-query/document-query-prefix.function';
import { DocumentQueryRow } from '../types/document-query';

export interface SalesDocumentSearchResult {
  id: string;
  documentType: string;
  documentNumber: number | null;
  customerName: string | null;
  customerNumber: number | null;
  documentDate: number | null;
}

export const DOCUMENT_SEARCH_DOCUMENT_TYPES = [
  'SALES_ORDER',
  'OFFER',
  'BLANKET_ORDER',
  'DELIVERY_NOTE',
  'INVOICE',
  'WORK_ORDER',
  'COMMISSION_NOTE',
] as const;

const MAX_RESULTS_PER_TYPE = 20;

/**
 * Global-search provider that looks up documents by document number across the
 * common document types. The document query endpoint is per document type, so
 * we fan out one query per type and merge the rows.
 */
@Injectable({ providedIn: 'root' })
export class SalesDocumentSearchProvider extends SearchResultProvider<SalesDocumentSearchResult> {
  private readonly dataAccess = inject(ApiDataAccess);

  override results(term: string | null): Observable<SalesDocumentSearchResult[]> {
    const documentNumber = this.parseDocumentNumber(term);
    if (documentNumber === null) return of([]);

    return forkJoin(
      DOCUMENT_SEARCH_DOCUMENT_TYPES.map((documentType) =>
        this.queryType(documentType, documentNumber)
      )
    ).pipe(map((perType) => perType.flat()));
  }

  private parseDocumentNumber(term: string | null): number | null {
    const trimmed = term?.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
  }

  private queryType(
    documentType: string,
    documentNumber: number
  ): Observable<SalesDocumentSearchResult[]> {
    const prefix = getDocumentQueryTypePrefix(documentType);
    const query: Query<DocumentQueryRow> = {
      columns: [
        { name: `${prefix}.id`, usage: 'group' },
        { name: `${prefix}.document_number`, usage: 'group' },
        { name: `${prefix}.document_date`, usage: 'group' },
        { name: 'customer.number', usage: 'group' },
        { name: 'customer.name', usage: 'group' },
      ] as unknown as Query<DocumentQueryRow>['columns'],
      sorts: [],
      filters: {
        type: 'stringEquals',
        name: `${prefix}.document_number`,
        value: String(documentNumber),
      } as unknown as Query<DocumentQueryRow>['filters'],
      startRow: 0,
      endRow: MAX_RESULTS_PER_TYPE,
      version: 1,
    };

    const dataSource = new DocumentGridDataSource(this.dataAccess, documentType);

    return from(dataSource.load({ query })).pipe(
      map((result) =>
        (result?.data ?? []).filter((row): row is DocumentQueryRow => !!row)
      ),
      map((rows) => rows.map((row) => this.toResult(row, documentType, prefix))),
      catchError(() => of([] as SalesDocumentSearchResult[]))
    );
  }

  private toResult(
    row: DocumentQueryRow,
    documentType: string,
    prefix: string
  ): SalesDocumentSearchResult {
    const doc = (row as unknown as Record<string, Record<string, unknown> | undefined>)[
      prefix
    ];
    const customer = row.customer;
    return {
      id: String(doc?.['id'] ?? ''),
      documentType,
      documentNumber: (doc?.['document_number'] as number | undefined) ?? null,
      customerName: customer?.name ?? null,
      customerNumber: customer?.number ?? null,
      documentDate: (doc?.['document_date'] as number | undefined) ?? null,
    };
  }
}
